import type { FighterDef } from './content'
import {VAELTHAR_ID} from '../../../../lib/character/src/vaelthar.js';
import type {VaeltharMind} from '../../../../lib/character/src/vaelthar-mind.js';
import {createVaeltharMind,type VaeltharPerception} from '../../../../lib/character/src/vaelthar-mind.js';
import {newVaeltharFightAI,stepVaeltharFightAI} from './vaelthar-fight';
import { characterDNAForFighter, characterDNACognitiveTraits } from './characterDNA'
import { deriveCharacterPhenotype } from './characterPhenotype'
import { quantumVacuumRuntimeProfile } from './quantumVacuum'
import { organFunction, perceptionConfidence, type LivingState } from './lifeOmega'
import { EMPTY_INTENT, STRIKES, type AttackKind, type AttackPhase, type Combatant, type Intent } from './simulation'

export type CognitiveProfile='standard'|'training-dummy'|'aggressive'
export type CognitiveSkillChoice=AttackKind|'guard'|'quantum'|'approach'|'retreat'|'jump'|'flight'|'dive'|'wait'

export type CognitiveTraits={
 aggression:number
 caution:number
 discipline:number
 patience:number
 creativity:number
 prediction:number
 courage:number
 adaptability:number
 aeriality:number
 precision:number
 learning:number
 inhibition:number
}

export type CognitiveSenses={
 vision:number
 hearing:number
 olfaction:number
 proprioception:number
 vestibular:number
 nociception:number
 interoception:number
 tactile:number
 thermal:number
 chemical:number
 electromagnetic:number
 echolocation:number
 vibration:number
 energetic:number
 spatial:number
 sensorFusion:number
 certainty:number
 threat:number
}

export type CognitiveMemory={
 observed:Record<AttackKind,number>
 successful:Record<AttackKind,number>
 lastTargetAttack:AttackKind|null
 lastTargetPhase:AttackPhase
 totalObserved:number
}

export type CognitiveAgentState={
 vaeltharMind?:VaeltharMind
 fighterId:string
 targetId:string|null
 traits:CognitiveTraits
 focus:number
 confidence:number
 fear:number
 frustration:number
 curiosity:number
 mentalLoad:number
 commitment:number
 decisionSerial:number
 lastChoice:CognitiveSkillChoice
 repeatCount:number
 lastDecisionConfidence:number
 predictedAttack:AttackKind|null
 memory:CognitiveMemory
 lastSelfHealth:number
 lastTargetHealth:number
}

export type CognitiveContext={
 self:Combatant
 target:Combatant
 selfLife:LivingState
 targetLife:LivingState
 selfDef:FighterDef
 targetDef:FighterDef
 tick:number
 profile:CognitiveProfile
 hazardPressure?:number
}

export type CognitiveDecision={
 intent:Intent
 state:CognitiveAgentState
 choice:CognitiveSkillChoice
 confidence:number
 senses:CognitiveSenses
 scores:Record<CognitiveSkillChoice,number>
 reason:string
 predictedAttack:AttackKind|null
}

export type CognitiveMotorBias={
 urgency:number
 arousal:number
 confidence:number
 curiosity:number
 threat:number
 focus:number
 caution:number
}

const ATTACKS:AttackKind[]=['light','heavy','special','throw']
const CHOICES:CognitiveSkillChoice[]=['special','throw','heavy','light','guard','quantum','flight','dive','jump','approach','retreat','wait']
const clamp=(v:number,lo=0,hi=1)=>Math.max(lo,Math.min(hi,Number.isFinite(v)?v:lo))
const clampScore=(v:number)=>Number.isFinite(v)?v:-999
const blend=(a:number,b:number,t:number)=>a+(b-a)*clamp(t)
const emptyAttackRecord=()=>({light:0,heavy:0,special:0,throw:0})

function hash32(input:string){
 let h=2166136261>>>0
 for(let i=0;i<input.length;i++){h^=input.charCodeAt(i);h=Math.imul(h,16777619)}
 h^=h>>>16;h=Math.imul(h,0x7feb352d);h^=h>>>15;h=Math.imul(h,0x846ca68b);h^=h>>>16
 return h>>>0
}
const unit=(id:string,salt:string)=>hash32(id+'|'+salt)/4294967295

export type CognitiveArchitectureRuntimeProfile={
 conscious:true
 memoryCapacity:number
 learningFactor:number
 decisionEntropy:number
 planningStrength:number
 selfControl:number
 goalPersistence:number
 memoryModel:number
 causalModel:number
 temporalModel:number
 spatialModel:number
 socialModel:number
 imagination:number
 language:number
 sensorFusion:number
}

export function cognitiveArchitectureProfile(fighterId:string):CognitiveArchitectureRuntimeProfile{
 const a=characterDNAForFighter(fighterId).mind.architecture
 const mind=characterDNAForFighter(fighterId).mind
 const memoryModel=clamp(a.workingMemory*.32+a.episodicMemory*.27+a.semanticMemory*.2+a.metacognition*.12+a.attentionControl*.09)
 const causalModel=clamp(a.causalReasoning*.5+a.planning*.25+a.metacognition*.15+a.workingMemory*.1)
 const temporalModel=clamp(a.temporalReasoning*.52+mind.prediction*.24+a.planning*.14+a.workingMemory*.1)
 const spatialModel=clamp(a.spatialReasoning*.5+a.sensorFusion*.3+a.attentionControl*.2)
 const socialModel=clamp(a.theoryOfMind*.5+a.socialCognition*.32+a.emotionalIntegration*.18)
 return{
  conscious:true,
  memoryCapacity:Math.max(4,Math.round(5+memoryModel*30)),
  learningFactor:clamp(.06+mind.learning*.1+a.metacognition*.07+a.episodicMemory*.04+a.semanticMemory*.03+a.emotionalIntegration*.03,.07,.36),
  decisionEntropy:clamp(1-(a.planning*.22+a.metacognition*.2+a.impulseControl*.16+a.selfModel*.12+a.attentionControl*.18+causalModel*.12)),
  planningStrength:clamp(a.planning*.3+a.workingMemory*.16+a.metacognition*.16+a.selfModel*.08+causalModel*.18+temporalModel*.12),
  selfControl:clamp(a.impulseControl*.48+a.metacognition*.2+a.emotionalIntegration*.17+a.attentionControl*.15),
  goalPersistence:clamp(a.goalPersistence*.55+a.volition*.25+a.selfModel*.12+a.attentionControl*.08),
  memoryModel,causalModel,temporalModel,spatialModel,socialModel,
  imagination:clamp(a.imagination),
  language:clamp(a.languageCapacity),
  sensorFusion:clamp(a.sensorFusion),
 }
}

function profileTraits(id:string):CognitiveTraits{
 const dna=characterDNACognitiveTraits(id)
 return{
  aggression:dna.aggression,caution:dna.caution,discipline:dna.discipline,patience:dna.patience,
  creativity:dna.creativity,prediction:dna.prediction,courage:dna.courage,adaptability:dna.adaptability,
  aeriality:dna.aeriality,precision:dna.precision,learning:dna.learning,inhibition:dna.inhibition,
 }
}

export function createCognitiveAgent(fighterId:string):CognitiveAgentState{
 return{
  fighterId,targetId:null,traits:profileTraits(fighterId),
   ...(fighterId===VAELTHAR_ID?{vaeltharMind:newVaeltharFightAI()}:{}),
  focus:.62,confidence:.58,fear:.14,frustration:0,curiosity:.42,mentalLoad:.18,commitment:.5,
  decisionSerial:0,lastChoice:'wait',repeatCount:0,lastDecisionConfidence:.5,predictedAttack:null,
  memory:{observed:emptyAttackRecord(),successful:emptyAttackRecord(),lastTargetAttack:null,lastTargetPhase:null,totalObserved:0},
  lastSelfHealth:-1,lastTargetHealth:-1,
 }
}

function attackSeverity(kind:AttackKind|null){
 switch(kind){case'special':return 1;case'heavy':return .78;case'throw':return .65;case'light':return .48;default:return .25}
}

function observePattern(state:CognitiveAgentState,target:Combatant){
 const memory={...state.memory,observed:{...state.memory.observed},successful:{...state.memory.successful}}
 const architectureRuntime=cognitiveArchitectureProfile(state.fighterId)
 if(target.attackPhase==='startup'&&memory.lastTargetPhase!=='startup'&&target.lastAttack){
  memory.observed[target.lastAttack]+=1
  memory.totalObserved+=1
  memory.lastTargetAttack=target.lastAttack
 }
 const memoryCapacity=architectureRuntime.memoryCapacity
 if(memory.totalObserved>memoryCapacity){
  const scale=memoryCapacity/memory.totalObserved
  for(const kind of ATTACKS)memory.observed[kind]*=scale
  memory.totalObserved=ATTACKS.reduce((sum,kind)=>sum+memory.observed[kind],0)
 }
 memory.lastTargetPhase=target.attackPhase
 let predicted:AttackKind|null=null,best=-1
 for(const kind of ATTACKS){if(memory.observed[kind]>best){best=memory.observed[kind];predicted=kind}}
 return{memory,predicted:memory.totalObserved>0?predicted:null}
}

function patternConfidence(memory:CognitiveMemory,fighterId:string){
 if(memory.totalObserved<=0)return 0
 const architecture=characterDNAForFighter(fighterId).mind.architecture
 const best=Math.max(...ATTACKS.map(kind=>memory.observed[kind]))
 const evidence=1-Math.exp(-memory.totalObserved/(2.6+architecture.workingMemory*3.2))
 const interpretation=.45+architecture.planning*.22+architecture.metacognition*.2+architecture.workingMemory*.13
 return clamp((best/memory.totalObserved)*evidence*interpretation)
}
function patternDiversity(memory:CognitiveMemory){
 if(memory.totalObserved<=1)return 0
 const active=ATTACKS.filter(kind=>memory.observed[kind]>0).length
 return clamp((active-1)/(ATTACKS.length-1))
}

export function cognitiveSenseFrame(state:CognitiveAgentState,context:CognitiveContext):CognitiveSenses{
 const life=context.selfLife,base=perceptionConfidence(life),stamina=clamp(context.self.stamina/100),superMeter=clamp(context.self.superMeter/100)
 const phenotype=deriveCharacterPhenotype(state.fighterId,life,context.self,state)
 const vision=phenotype.sensoryExpression.vision,hearing=phenotype.sensoryExpression.hearing,olfaction=phenotype.sensoryExpression.olfaction
 const proprioception=phenotype.sensoryExpression.proprioception,vestibular=phenotype.sensoryExpression.vestibular,nociception=phenotype.painLoad
 const interoception=clamp(life.consciousness*.24+life.oxygenation*.2+life.circulation*.18+(1-life.fatigue)*.2+stamina*.18)
 const tactile=clamp(phenotype.sensoryExpression.tactile+(life.reflexTimer>0?Math.min(.22,life.reflexTimer/.28*.22):0))
 const thermal=phenotype.sensoryExpression.thermal,chemical=phenotype.sensoryExpression.chemical
 const electromagnetic=phenotype.sensoryExpression.electromagnetic,echolocation=phenotype.sensoryExpression.echolocation
 const vibration=phenotype.sensoryExpression.vibration
 const energetic=clamp(phenotype.sensoryExpression.energetic*.7+organFunction(life,'energy')*.15+superMeter*.15)
 const spatial=phenotype.sensoryExpression.spatial,sensorFusion=phenotype.cognitiveExpression.sensorFusion
 const ambientAwareness=clamp(
  olfaction*.08+thermal*.08+chemical*.06+electromagnetic*.08+echolocation*.08+vibration*.1+
  hearing*.12+vision*.18+spatial*.22
 )
 const certainty=clamp(
  base*.22+spatial*.25+ambientAwareness*.16+sensorFusion*.12+interoception*.1+state.focus*.15-
  nociception*.1-state.mentalLoad*.08
 )
 const dx=context.target.x-context.self.x,dz=context.target.z-context.self.z,dy=context.target.y-context.self.y
 const distance=Math.hypot(dx,dz*.85,dy*.6),close=1-clamp((distance-.55)/4.4)
 const attackSignal=context.target.attackPhase?attackSeverity(context.target.lastAttack)*(context.target.attackPhase==='active'?1:.78):0
 const localDisturbance=clamp(vibration*.32+hearing*.2+thermal*.1+electromagnetic*.12+echolocation*.14+olfaction*.06+chemical*.06)
 const lowHealth=1-clamp(context.self.health/Math.max(1,context.self.maxHealth))
 const threat=clamp(
  attackSignal*(.44+.34*spatial+.12*sensorFusion)+close*(.12+.08*localDisturbance)+
  Math.min(.22,context.target.comboCount*.042)+(context.hazardPressure??0)*(.18+.06*ambientAwareness)+lowHealth*.18
 )
 return{vision,hearing,olfaction,proprioception,vestibular,nociception,interoception,tactile,thermal,chemical,electromagnetic,echolocation,vibration,energetic,spatial,sensorFusion,certainty,threat}
}

function updateMind(previous:CognitiveAgentState,context:CognitiveContext,senses:CognitiveSenses){
 const observed=observePattern(previous,context.target),memory=observed.memory
 const selfHealth=context.self.maxHealth>0?clamp(context.self.health/context.self.maxHealth):0,targetHealth=context.target.maxHealth>0?clamp(context.target.health/context.target.maxHealth):0
 const previousSelf=previous.lastSelfHealth<0?context.self.health:previous.lastSelfHealth,previousTarget=previous.lastTargetHealth<0?context.target.health:previous.lastTargetHealth
 const hurt=clamp((previousSelf-context.self.health)/Math.max(1,context.self.maxHealth))
 const damageDone=clamp((previousTarget-context.target.health)/Math.max(1,context.target.maxHealth))
 if(damageDone>.002&&ATTACKS.includes(previous.lastChoice as AttackKind))memory.successful[previous.lastChoice as AttackKind]+=1
 const dna=characterDNAForFighter(previous.fighterId),architecture=dna.mind.architecture,architectureRuntime=cognitiveArchitectureProfile(previous.fighterId)
 const pConfidence=patternConfidence(memory,previous.fighterId),diversity=patternDiversity(memory),traits=previous.traits
 const fearTarget=clamp(senses.threat*(.46+traits.caution*.42)+(1-selfHealth)*.3+senses.nociception*.18-hurt*0+(-traits.courage*.24))
 const loadTarget=clamp((1-senses.certainty)*.34+senses.threat*.24+senses.nociception*.2+context.selfLife.fatigue*.16+(context.hazardPressure??0)*.16)
 const focusTarget=clamp(traits.discipline*.3+senses.certainty*.4+pConfidence*.16+(senses.threat>.45?.1:0)-loadTarget*.13)
 const confidenceTarget=clamp(.18+selfHealth*.22+(selfHealth-targetHealth)*.18+senses.certainty*.18+traits.courage*.22+damageDone*.72-hurt*.95-fearTarget*.12)
 const frustrationTarget=clamp(previous.frustration*.8+(damageDone<.001&&previous.lastChoice!=='wait'?.035:0)+hurt*.42-damageDone*.52)
 const curiosityTarget=clamp(traits.creativity*.5+diversity*.24+(1-pConfidence)*traits.adaptability*.2+frustrationTarget*.12)
 const commitmentTarget=clamp(
  confidenceTarget*.25+traits.aggression*.18+traits.courage*.13+focusTarget*.1+frustrationTarget*.08+
  architecture.volition*.1+architecture.goalPersistence*.1+architecture.selfModel*.06-
  fearTarget*(.18-architecture.emotionalIntegration*.06)
 )
 const learning=architectureRuntime.learningFactor
 const lastChoice=previous.lastChoice,repeatCount=previous.repeatCount
 return{
  ...previous,memory,predictedAttack:observed.predicted,
  focus:blend(previous.focus,focusTarget,learning),confidence:blend(previous.confidence,confidenceTarget,learning),
  fear:blend(previous.fear,fearTarget,learning),frustration:blend(previous.frustration,frustrationTarget,learning),
  curiosity:blend(previous.curiosity,curiosityTarget,learning),mentalLoad:blend(previous.mentalLoad,loadTarget,learning),
  commitment:blend(previous.commitment,commitmentTarget,learning),
  lastChoice,repeatCount,lastSelfHealth:context.self.health,lastTargetHealth:context.target.health,
 }
}

function rangeAffinity(kind:AttackKind,self:Combatant,target:Combatant){
 const dx=Math.abs(target.x-self.x),dz=Math.abs(target.z-self.z),dy=Math.abs(target.y-self.y)
 const distance=Math.hypot(dx,dz*.9),strike=STRIKES[kind],verticalReach=kind==='special'?2.4:kind==='heavy'?1.75:kind==='throw'?1.15:1.4
 const horizontal=clamp(1-Math.max(0,distance-strike.range)/(strike.range*1.15))
 const vertical=clamp(1-dy/verticalReach)
 return horizontal*vertical
}

function successBias(state:CognitiveAgentState,kind:AttackKind){
 const observed=state.memory.successful[kind],attemptProxy=Math.max(1,state.memory.observed[kind])
 return clamp(observed/attemptProxy)*.16
}
function repetitionPenalty(state:CognitiveAgentState,choice:CognitiveSkillChoice){
 if(state.lastChoice!==choice)return 0
 const architectureRuntime=cognitiveArchitectureProfile(state.fighterId)
 const selfCorrection=.34+state.traits.adaptability*.28+state.traits.creativity*.12+architectureRuntime.selfControl*.26
 return Math.min(.38,state.repeatCount*.052*selfCorrection)
}

function scoreChoices(state:CognitiveAgentState,context:CognitiveContext,senses:CognitiveSenses){
 const t=state.traits,self=context.self,target=context.target,dna=characterDNAForFighter(state.fighterId),phenotype=deriveCharacterPhenotype(state.fighterId,context.selfLife,self,state),brain=cognitiveArchitectureProfile(state.fighterId),vacuum=quantumVacuumRuntimeProfile(state.fighterId,phenotype,self)
 const dx=target.x-self.x,dz=target.z-self.z,dy=target.y-self.y,distance=Math.hypot(dx,dz*.9)
 const far=clamp((distance-1.3)/5),close=1-far,selfHealth=clamp(self.health/Math.max(1,self.maxHealth)),targetHealth=clamp(target.health/Math.max(1,target.maxHealth))
 const desperation=clamp((1-selfHealth)*.52+state.fear*.18+state.frustration*.3),finisher=clamp((.42-targetHealth)/.42)
 const uncertainty=1-senses.certainty,threat=senses.threat,pattern=patternConfidence(state.memory,state.fighterId)
 const predicted=state.predictedAttack,highThreatPattern=predicted==='heavy'||predicted==='special'
 const can=(kind:AttackKind)=>self.stamina>=STRIKES[kind].stamina&&(kind!=='special'||self.superMeter>=100)&&!self.downed&&self.hitstun<=0
 const scores:Record<CognitiveSkillChoice,number>={
  light:-999,heavy:-999,special:-999,throw:-999,guard:-999,quantum:-999,approach:-999,retreat:-999,jump:-999,flight:-999,dive:-999,wait:.08,
 }
 if(can('light'))scores.light=rangeAffinity('light',self,target)*(.48+state.focus*.46+senses.certainty*.28+t.precision*.18)*(.55+phenotype.skillReadiness.light*.55)+(1-self.stamina/100)*.06+successBias(state,'light')
 if(can('heavy'))scores.heavy=rangeAffinity('heavy',self,target)*(.34+t.aggression*.48+state.confidence*.32+state.commitment*.3+(target.guardMeter<36?.28:0))*(.55+phenotype.skillReadiness.heavy*.55)-threat*t.caution*.16-uncertainty*t.inhibition*.24+successBias(state,'heavy')
 if(can('throw'))scores.throw=rangeAffinity('throw',self,target)*(.24+(target.blocking?.84:0)+state.focus*.18+t.prediction*.14+pattern*.1+brain.socialModel*.07+brain.causalModel*.07)*(.55+phenotype.skillReadiness.throw*.55)+successBias(state,'throw')
 if(can('special'))scores.special=rangeAffinity('special',self,target)*(.24+t.aggression*.2+t.creativity*.15+state.confidence*.2+state.commitment*.22+finisher*.52+desperation*.2+brain.planningStrength*.08+brain.causalModel*.07+brain.imagination*.04)*(.52+phenotype.skillReadiness.special*.62)-uncertainty*t.inhibition*.22+successBias(state,'special')
 if(self.guardMeter>0&&!self.downed)scores.guard=threat*(.5+t.caution*.5+t.discipline*.24)+pattern*(highThreatPattern?.26:.08)+(target.attackPhase==='startup'?.22:target.attackPhase==='active'?.3:0)+brain.planningStrength*.08+brain.selfControl*.06-state.commitment*.08
 if(dna.capabilities.quantum&&vacuum.readiness>.05&&(self.quantumCooldown??0)<=0&&self.stamina>=vacuum.cost&&!self.downed)scores.quantum=(threat*(.27+t.prediction*.25+t.creativity*.1+brain.temporalModel*.16+brain.planningStrength*.07+vacuum.perception*.07)+(self.guardMeter<25?.26:0)+pattern*(highThreatPattern?.2:.04)-t.inhibition*.05)*(.48+vacuum.readiness*.58+vacuum.stability*.08-vacuum.decoherenceRisk*.14)
 scores.approach=far*(.31+t.aggression*.3+state.commitment*.24+state.confidence*.14+brain.spatialModel*.08+brain.planningStrength*.05)+(Math.abs(dy)>.7?.08:0)
 scores.retreat=threat*(.38+t.caution*.48)+uncertainty*.22+(1-selfHealth)*.2+state.fear*.22-t.courage*.13
 scores.jump=(self.aerialMode==='ground'?1:0)*clamp((Math.max(0,dy-.25)*.38+threat*.15+t.aeriality*.28+t.creativity*.1)*senses.proprioception)
 scores.flight=(dna.capabilities.flight&&phenotype.flightReadiness>.05&&self.aerialMode!=='flight'&&self.y>.42?1:0)*clamp(t.aeriality*.38+Math.max(0,dy)*.2+far*.1+state.curiosity*.1+threat*.06+dna.combat.aerialMastery*.18+phenotype.flightReadiness*.28)*senses.vestibular
 scores.dive=(self.aerialMode!=='ground'&&self.y>target.y+.45?1:0)*clamp(t.aggression*.36+state.commitment*.28+close*.28+senses.spatial*.16+dna.combat.aerialMastery*.16)
 scores.wait=.08+t.patience*.1+(1-threat)*uncertainty*.16+state.mentalLoad*.08
 if(context.profile==='training-dummy'){
  for(const kind of ATTACKS)scores[kind]=-999
  scores.approach=-999;scores.retreat=-999;scores.jump=-999;scores.flight=-999;scores.dive=-999;scores.quantum=-999
  scores.guard=target.attackPhase?1.2:.18;scores.wait=target.attackPhase?.1:.72
 }else if(context.profile==='aggressive'){
  scores.light+=.12;scores.heavy+=.18;scores.special+=.22;scores.throw+=.1;scores.approach+=.16;scores.retreat-=.18;scores.guard-=.06
 }
 for(const choice of CHOICES)scores[choice]=clampScore(scores[choice]-repetitionPenalty(state,choice))
 return scores
}

function choose(scores:Record<CognitiveSkillChoice,number>,state:CognitiveAgentState,tick:number){
 const ranked=CHOICES.map(choice=>({choice,score:scores[choice]})).sort((a,b)=>b.score-a.score||a.choice.localeCompare(b.choice))
 const architecture=characterDNAForFighter(state.fighterId).mind.architecture,architectureRuntime=cognitiveArchitectureProfile(state.fighterId)
 const bandWidth=.025+architectureRuntime.decisionEntropy*.095
 const top=ranked[0],second=ranked[1],band=ranked.filter(item=>item.score>=top.score-bandWidth)
 const pick=band[hash32(state.fighterId+'|'+state.decisionSerial+'|'+Math.floor(tick/2))%band.length]
 const margin=Math.max(0,pick.score-second.score)
 const confidence=clamp(.36+margin*.72+state.focus*.14+architecture.selfModel*.08+architecture.metacognition*.08-state.mentalLoad*.12)
 return{choice:pick.choice,confidence,top:pick.score,second:second.score}
}

function intentFor(choice:CognitiveSkillChoice,context:CognitiveContext):Intent{
 const self=context.self,target=context.target,dx=target.x-self.x,dz=target.z-self.z,distance=Math.hypot(dx,dz*.9)
 const toward={left:dx<0,right:dx>0,depthUp:dz<-.18,depthDown:dz>.18}
 switch(choice){
  case'light':return{...EMPTY_INTENT,light:true}
  case'heavy':return{...EMPTY_INTENT,heavy:true}
  case'special':return{...EMPTY_INTENT,special:true}
  case'throw':return{...EMPTY_INTENT,throw:true}
  case'guard':return{...EMPTY_INTENT,block:true}
  case'quantum':return{...EMPTY_INTENT,quantum:true}
  case'jump':return{...EMPTY_INTENT,...toward,jump:true}
  case'flight':return{...EMPTY_INTENT,...toward,flight:true}
  case'dive':return{...EMPTY_INTENT,...toward,dive:true,boost:self.aerialMode==='flight'}
  case'approach':return{...EMPTY_INTENT,...toward,dash:distance>4&&self.stamina>38,boost:self.aerialMode==='flight'}
  case'retreat':return{...EMPTY_INTENT,left:dx>0,right:dx<0,depthUp:dz>.18,depthDown:dz<-.18,dash:self.stamina>46}
  default:return EMPTY_INTENT
 }
}

function decideVaeltharCombat(updated:CognitiveAgentState,context:CognitiveContext,senses:CognitiveSenses,scores:Record<CognitiveSkillChoice,number>):CognitiveDecision{
 const self=context.self,target=context.target;
 const distance=Math.hypot(target.x-self.x,(target.z-self.z)*.9);
 const worldSec=Math.max(0,context.tick/60);
 const previous=updated.vaeltharMind&&updated.vaeltharMind.worldSec<=worldSec+.15?
  updated.vaeltharMind:createVaeltharMind(worldSec);
 const specialReady=self.superMeter>=100&&self.attackPhase===null&&self.cooldown<=0;
 const canAttack=!self.downed&&self.hitstun<=0&&self.attackPhase===null&&self.cooldown<=0&&
  distance<=(specialReady?STRIKES.special.range:STRIKES.heavy.range);
 const perception:Omit<VaeltharPerception,'game'>={
  worldSec,hostiles:1,nearestHostileMeters:distance,immediateThreat:senses.threat,
  alliedLowHealth:0,civiliansAtRisk:0,unknownStarSignals:0,canTravel:false,
  healthFraction:clamp(self.health/Math.max(1,self.maxHealth)),
  manaFraction:previous.mana/100,targetId:context.targetDef.id,
  attackReady:canAttack,specialReady,ultimateReady:specialReady,
 };
 const result=stepVaeltharFightAI(previous,perception),spell=result.decision.skillId;
 let choice:CognitiveSkillChoice=
  spell==='astral-cathedral'||spell==='orbital-seal'?'special':
  spell==='constellation-breaker'?'heavy':
  spell==='astral-thread'?'light':
  spell==='star-step'?'retreat':
  spell==='astrolabe-guard'?'guard':
  distance>STRIKES.heavy.range?'approach':
  senses.threat>.45?'guard':'wait';
 if(choice==='special'&&(!specialReady||distance>STRIKES.special.range))choice='approach';
 if(choice==='heavy'&&distance>STRIKES.heavy.range)choice='approach';
 if(choice==='light'&&distance>STRIKES.light.range)choice='approach';
 if(self.downed||self.hitstun>0)choice='wait';
 scores[choice]=Math.max(1.05,scores[choice]);
 const repeatCount=choice===updated.lastChoice?updated.repeatCount+1:0;
 const state:CognitiveAgentState={...updated,vaeltharMind:result.state,
  targetId:context.targetDef.id,lastChoice:choice,repeatCount,
  decisionSerial:updated.decisionSerial+1,lastDecisionConfidence:result.decision.confidence};
 return{intent:intentFor(choice,context),state,choice,
  confidence:result.decision.confidence,senses,scores,
  reason:'vaelthar:'+result.decision.reason+'|skill='+(spell??'none'),
  predictedAttack:state.predictedAttack};
}

export function decideCognitiveIntent(previous:CognitiveAgentState,context:CognitiveContext):CognitiveDecision{
 if(previous.fighterId!==context.selfDef.id||previous.targetId!==context.targetDef.id){previous=createCognitiveAgent(context.selfDef.id);previous={...previous,targetId:context.targetDef.id}}
 const preSenses=cognitiveSenseFrame(previous,context),updated=updateMind(previous,context,preSenses)
 const senses=cognitiveSenseFrame(updated,context),scores=scoreChoices(updated,context,senses),selection=choose(scores,updated,context.tick)
 const repeatCount=selection.choice===updated.lastChoice?updated.repeatCount+1:0
 const state={...updated,targetId:context.targetDef.id,lastChoice:selection.choice,repeatCount,decisionSerial:updated.decisionSerial+1,lastDecisionConfidence:selection.confidence}
 const predicted=state.predictedAttack
 const reason=[
  'choice='+selection.choice,
  'certainty='+senses.certainty.toFixed(2),
  'threat='+senses.threat.toFixed(2),
  'focus='+state.focus.toFixed(2),
  'confidence='+state.confidence.toFixed(2),
  'fear='+state.fear.toFixed(2),
  predicted?'predict='+predicted:'predict=none',
 ].join('|')
 if(state.fighterId===VAELTHAR_ID&&context.profile!=='training-dummy'&&context.tick%12===0)
  return decideVaeltharCombat(updated,context,senses,scores);

return{intent:intentFor(selection.choice,context),state,choice:selection.choice,confidence:selection.confidence,senses,scores,reason,predictedAttack:predicted}
}

export function cognitiveMotorBias(state:CognitiveAgentState):CognitiveMotorBias{
 return{
  urgency:clamp(state.commitment*.36+state.fear*.24+state.frustration*.22+state.mentalLoad*.18),
  arousal:clamp(state.commitment*.34+state.fear*.28+state.frustration*.22+state.confidence*.16),
  confidence:clamp(state.confidence),curiosity:clamp(state.curiosity),
  threat:clamp(state.fear*.52+state.mentalLoad*.18+(1-state.confidence)*.12+state.commitment*.18),
  focus:clamp(state.focus),caution:clamp(state.traits.caution),
 }
}

export function cognitiveTelemetry(state:CognitiveAgentState,senses:CognitiveSenses){
 return{
  fighterId:state.fighterId,decisionSerial:state.decisionSerial,lastChoice:state.lastChoice,
  decisionConfidence:state.lastDecisionConfidence,predictedAttack:state.predictedAttack,
  focus:state.focus,confidence:state.confidence,fear:state.fear,frustration:state.frustration,
  curiosity:state.curiosity,mentalLoad:state.mentalLoad,commitment:state.commitment,
  certainty:senses.certainty,threat:senses.threat,
  cognitiveArchitecture:{...characterDNAForFighter(state.fighterId).mind.architecture},
  cognitiveRuntime:cognitiveArchitectureProfile(state.fighterId),
  quantumVacuumDNA:{...characterDNAForFighter(state.fighterId).power.quantumVacuum},
 }
}
